import Apisauce from 'apisauce';
import {cleanObj, isNonEmptyArray, isNonEmptyObject, isNonEmptyString} from "@tomeravni/easybizy-js-common/common";
import {adaptGenderToServer} from "./Common/Helpers";
import {exploreToken} from "./Common/ExploreAttribution";

const create = (baseURL = '/api/') => {

    const api = Apisauce.create({
        baseURL,
        headers: {
            'Cache-Control': 'no-cache'
        },
        // 60 seconds timeout...
        timeout: 60000
    });

    const getMeetingReservationConfig = async (context, customerToken, includeServices = false) => {
        const includeServicesStr = includeServices ? '?&includeServices=true' : '';
        const customerTokenStr = customerToken ? `/${customerToken}` : '';
        const {
            data,
            ok,
            error
        } = await api.get(`/context/${context}${customerTokenStr}${includeServicesStr}`);
        return {data, ok, error: ok ? error : (error || data)};
    };

    const getConfigByContextName = async (token, byName) => {
        const {data, ok, error} = await api.get(`/resolve-context/${token}${byName ? '?byName=true' : ''}`);
        return {data, ok, error: ok ? error : (error || data)};
    };

    const resolveContext = async (possibleContextName, token) => {
        let url = '/resolve-context';
        if (token) {
            url += `?token=${token}`;
        }

        if (possibleContextName) {
            url += token ? '&' : '?';
            url += `name=${possibleContextName}`;
        }

        const {
            data,
            ok,
            error
        } = await api.get(url);
        return {data, ok, error: ok ? error : (error || data)};
    }

    const getSession = async (token) => {
        const {data, ok, error} = await api.get(`/sessions/${token}`);
        return {data, ok, error: ok ? error : (error || data)};
    };

    const getInstantOptions = async (token, serviceIds, employeeId) => {
        const {
            data,
            ok,
            error
        } = await api.get(`/instant-options?token=${token}&service_ids=${serviceIds}&dayPart=${'111'}&employeeId=${employeeId}`);
        return {data, ok, error: ok ? error : (error || data)};
    };

    const getSpecificOptions = async (token, serviceIds, employeeId, dayPart, date, existingMode, existingMeetingIds, genderMap, quantityMap) => {
        let customExistingQuery = '';
        if (isNonEmptyArray(existingMeetingIds)) {
            customExistingQuery = `&existingMode=${existingMode}&existingMeetingIds=${existingMeetingIds.join(',')}`;
        }

        if (isNonEmptyObject(genderMap)) {
            const adaptedGenderQuery = Object.keys(genderMap).map((curr) => {
                return `${curr}-${genderMap[curr].map(adaptGenderToServer).join('_')}`;
            }).join(',');

            customExistingQuery += `&gender=${adaptedGenderQuery}`;
        }

        if (employeeId > 0) {
            customExistingQuery += `&employeeId=${employeeId}`;
        }

        if (isNonEmptyObject(quantityMap)) {
            const adaptedQuantityQuery = Object.keys(quantityMap).map((curr) => {
                return `${curr}-${quantityMap[curr]}`;
            }).join(',');

            customExistingQuery += `&quantity=${adaptedQuantityQuery}`;
        }

        const {
            data,
            ok,
            error
        } = await api.get(`/specific-options?token=${token}&service_ids=${serviceIds}&dayPart=${dayPart}&date=${date}${customExistingQuery}`);
        return {data, ok, error: ok ? error : (error || data)};
    };

    const scheduleMeeting = async (token, serviceIds, employeeId, start, end, replaceMeetings,
                                   internalOptions, roomId, remarks, chosenEmployeeId, progressValues, gendersMap, quantityMap, userSelection) => {
        const {data, ok, error} = await api.post(`/schedule`, {
            token,
            serviceIds,
            employeeId,
            start,
            end,
            chosenEmployeeId,
            roomId,
            replaceMeetings,
            internalOptions,
            remarks,
            progressValues,
            gendersMap,
            quantityMap,
            userSelection,
            analyticsToken: exploreToken()
        });

        return {data, ok, error: ok ? error : (error || data)};
    };

    const registerWaitingListItem = async (token, waitingListItem, userSelection) => {
        const {data, ok, error} = await api.post(`/waiting-list`, {
            token,
            waitingListItem,
            userSelection
        });

        return {data, ok, error: ok ? error : (error || data)};
    };

    const removeWaitingListItem = async (token, waitingListItemId) => {
        const {data, ok, error} = await api.delete(`/waiting-list`, {
            token,
            waitingListItemId
        });

        return {data, ok, error: ok ? error : (error || data)};
    };

    const removeMeetings = async (token, meetings) => {
        let stringMeetings = JSON.stringify(meetings);
        const {data, ok, error} = await api.delete(`/schedule`, {
            token,
            meetings: stringMeetings
        });

        return {data, ok, error: ok ? error : (error || data)};
    };

    const validatePhone = async (token, validationMethod) => {
        const {data, ok, error} = await api.post(`/validate`, Object.assign({token}, validationMethod));
        return {data, ok, error: ok ? error : (error || data)};
    };


    const resendPhoneToken = async (token, hash) => {
        const {data, ok, error} = await api.post(`/revalidate-phone`, {token, hash});
        return {data, ok, error: ok ? error : (error || data)};
    };

    const validateCode = async (token, hash, code) => {
        const {data, ok, error} = await api.post(`/validate-code`, cleanObj({token, hash, code, analyticsToken: exploreToken()}));
        return {data, ok, error: ok ? error : (error || data)};
    };

    const createCustomer = async (token, hash, fullName, identifyNumber, mobile, email) => {
        const {data, ok, error} = await api.post(`/new-customer`, cleanObj({
            token,
            hash,
            fullName,
            identifyNumber,
            mobile,
            email,
            analyticsToken: exploreToken()
        }));

        return {data, ok, error: ok ? error : (error || data)};
    };

    const getManifest = async (token, contextName) => {
        let urlPostfix = '?';
        if (isNonEmptyString(token)) {
            urlPostfix += `token=${token}`;
        } else if (isNonEmptyString(contextName)) {
            urlPostfix += `context=${contextName}`;
        }

        const {data, ok, error} = await api.get(`/manifest${urlPostfix}`);
        return {data, ok, error: ok ? error : (error || data)};
    }

    const confirmIdentity = async (contextName, customerToken) => {
        const {data, ok, error} = await api.post(`/identity/confirm`, {contextName, customerToken});
        return {data, ok, error: ok ? error : (error || data)};
    }

    const rejectIdentity = async (contextName, customerToken) => {
        const {data, ok, error} = await api.post(`/identity/reject`, {contextName, customerToken});
        return {data, ok, error: ok ? error : (error || data)};
    }

    const cancelSession = async (token) => {
        const {data, ok, error} = await api.delete(`/sessions/${token}`);
        return {data, ok, error: ok ? error : (error || data)};
    }

    return {
        getMeetingReservationConfig,
        getInstantOptions,
        getSpecificOptions,
        scheduleMeeting,
        removeMeetings,
        validatePhone,
        validateCode,
        createCustomer,
        getConfigByContextName,
        resolveContext,
        getSession,
        getManifest,
        resendPhoneToken,
        cancelSession,
        confirmIdentity,
        rejectIdentity,
        registerWaitingListItem,
        removeWaitingListItem,
        baseURL: api.getBaseURL()
    }
};

export default create();
